import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import { Field, Fields, reduxForm } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import IconButton from 'components/common/IconButton';
import IconPrint from 'components/icons/IconPrint';
import Menu, { MenuItem } from 'components/common/Menu';

import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

import ScheduleCalendarFilterDateOfDeliveryFields from './ScheduleCalendarFilterDateOfDeliveryFields';
import ScheduleCalendarFilterPrevNextControls from './ScheduleCalendarFilterPrevNextControls';

const styles = theme => ({
  root: {
    position: 'relative',
  },
  paper: {
    position: 'relative',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    '&:before': {
      content: "''",
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: 95,
      height: 28,
      backgroundColor: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.primary.A100}`,
      zIndex: 1,
      [theme.breakpoints.down('xs')]: {
        width: 38,
      },
    },
  },
  paperVariantSingleDate: {
    '&:before': {
      width: 200,
    },
  },
  shadowContainer: {
    position: 'absolute',
    zIndex: -1,
    overflow: 'hidden',
    top: -20,
    right: -20,
    bottom: 0,
    left: -20,
    padding: '20px 20px 0 20px',
  },
  shadow: {
    width: '100%',
    height: '100%',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  form: {
    marginLeft: 95,
    position: 'relative',
    paddingRight: 8,
    zIndex: 2,
    borderLeft: `1px solid ${theme.palette.primary.A100}`,
    [theme.breakpoints.down('xs')]: {
      marginLeft: 38,
    },
  },
  formVariantSingleDate: {
    marginLeft: 200,
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  prevNextControls: {
    margin: '5px 0 4px 8px',
  },
  dateRangeFields: {
    margin: '10px 0 10px 24px',
  },
  iconPrint: {
    width: 22,
    height: 20,
  },
  tableContainer: {
    overflow: 'hidden',
  },
  rowHead: {
    height: 27,
  },
  cell: {
    padding: '6px 2px 5px',
    minWidth: 44,
    textAlign: 'center',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    borderRight: '1px solid transparent',
    userSelect: 'none',
    '&:last-child': {
      paddingRight: 2,
    },
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  cellCaption: {
    fontSize: 0,
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    backgroundColor: theme.palette.common.white,
    paddingRight: 24,
    minWidth: 47,
    width: 47,
    '&:last-child': {
      paddingRight: 24,
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: 4,
      minWidth: 30,
    },
  },
  cellCaptionVariantSingleDate: {
    minWidth: 173,
    width: 173,
    paddingRight: 2,
  },
  label: {
    position: 'relative',
    marginLeft: 24,
    fontSize: pxToRem(14),
    lineHeight: pxToEm(16, 14),
    letterSpacing: pxToEm(0.5),
    fontWeight: 400,
    color: theme.palette.text.primary,
    transform: 'none',
  },
  formControlVariant: {
    marginTop: 10,
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
});

const validateForm = (values) => {
  const errors = {};

  if (!values['Date of Delivery From']) {
    errors['Date of Delivery From'] = 'Field is required';
  }

  if (!values['Date of Delivery To']) {
    errors['Date of Delivery To'] = 'Field is required';
  }

  if (values['Date of Delivery From'] && !errors['Date of Delivery From']) {
    errors['Date of Delivery From'] = validate.date(values['Date of Delivery From']);
  }

  if (values['Date of Delivery To'] && !errors['Date of Delivery To']) {
    errors['Date of Delivery To'] = validate.date(values['Date of Delivery To']);
  }

  if (values['Date of Delivery From']
    && !validate.date(values['Date of Delivery From'])
    && values['Date of Delivery To']
    && !validate.date(values['Date of Delivery To'])
    && moment(values['Date of Delivery To'], dateFormat).isBefore(moment(values['Date of Delivery From'], dateFormat))) {
    errors['Date of Delivery To'] = 'Date To should be after Date From';
  }

  return errors;
};

class ScheduleCalendarFilter extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
    };

    this.onPrintMenuButtonClick = this.onPrintMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
  }

  onPrintMenuButtonClick(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  render() {
    const {
      handleSubmit,
      variant,
      submit,
      disabled,
      onEditPreviewButtonClick,
      horizontalScroll,
      printDisabled,
      classes,
    } = this.props;
    const { anchorEl } = this.state;
    const headers = [...new Array(24)].map((item, index) => index);
    return (
      <div className={classes.root}>
        <Paper
          className={classNames(classes.paper, {
            [classes.paperVariantSingleDate]: variant === 'single-date',
          })}
          elevation={0}
        >
          <form
            className={classNames(classes.form, {
              [classes.formVariantSingleDate]: variant === 'single-date',
            })}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <Grid
              container
              alignItems="center"
              spacing={0}
              wrap="nowrap"
            >
              <Grid
                className={classes.gridItemShrink}
                item
                xs={12}
              >
                <Grid
                  container
                  justify="space-between"
                  alignItems="center"
                  wrap="nowrap"
                  spacing={24}
                >
                  <Grid item>
                    <Grid
                      container
                      justify="space-between"
                      alignItems="center"
                      wrap="nowrap"
                      spacing={0}
                    >
                      <Grid item>
                        <div className={classes.dateRangeFields}>
                          <Fields
                            names={[
                              'Date of Delivery From',
                              'Date of Delivery To',
                            ]}
                            id="date-from-to"
                            component={ScheduleCalendarFilterDateOfDeliveryFields}
                          />
                        </div>
                      </Grid>
                      <Grid item>
                        <Fields
                          className={classes.prevNextControls}
                          names={[
                            'Date of Delivery From',
                            'Date of Delivery To',
                            'variant',
                          ]}
                          component={ScheduleCalendarFilterPrevNextControls}
                          disabled={disabled}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      justify="space-between"
                      alignItems="center"
                      wrap="nowrap"
                      spacing={8}
                    >
                      <Grid item>
                        <FormControl
                          className={classes.formControlVariant}
                          role="group"
                        >
                          <Field
                            name="variant"
                            component={RadioGroupField}
                          >
                            <RadioButtonField
                              value="day"
                              label="Day"
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                }
                              }}
                            />
                            <RadioButtonField
                              value="week"
                              label="Week"
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                }
                              }}
                            />
                            <RadioButtonField
                              value="month"
                              label="Month"
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                }
                              }}
                            />
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <IconButton
                          tooltipContent="Print Schedule"
                          onClick={this.onPrintMenuButtonClick}
                          aria-label="More"
                          aria-owns={anchorEl ? 'schedule-print-more-menu' : null}
                          aria-haspopup="true"
                          disabled={printDisabled}
                        >
                          <IconPrint className={classes.iconPrint} />
                        </IconButton>
                        <Menu
                          id="schedule-print-more-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={this.onMenuClose}
                          list
                        >
                          <MenuItem
                            onClick={() => {
                              this.onMenuClose();
                              onEditPreviewButtonClick();
                            }}
                          >
                            Edit / Preview
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              submit();
                              this.onMenuClose();
                            }}
                          >
                            Print
                          </MenuItem>
                        </Menu>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
          <div className={classes.tableContainer}>
            <Table
              style={{
                transform: `translateX(${-horizontalScroll}px)`,
              }}
            >
              <TableHead>
                <TableRow className={classes.rowHead}>
                  <TableCell
                    className={classNames(classes.cell, classes.cellCaption, {
                      [classes.cellCaptionVariantSingleDate]: variant === 'single-date',
                    })}
                  >
                    {variant === 'single-date' ? 'Driver Name' : 'Week Day'}
                  </TableCell>
                  {headers.map((halfHour) => {
                    const hour = 8 + Math.floor(halfHour / 2);
                    const minute = (halfHour % 2) * 30;
                    if (minute === 0) {
                      const formattedTime = moment({ hour, minute }).format('h A');
                      return (
                        <TableCell
                          key={halfHour}
                          className={classes.cell}
                        >
                          {formattedTime}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={halfHour}
                        className={classes.cell}
                      />
                    );
                  })}
                </TableRow>
              </TableHead>
            </Table>
          </div>
        </Paper>
        <div
          className={classes.shadowContainer}
          role="presentation"
        >
          <Paper className={classes.shadow} />
        </div>
      </div>
    );
  }
}

ScheduleCalendarFilter.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['single-date', 'date-range']).isRequired,
  disabled: PropTypes.bool.isRequired,
  onEditPreviewButtonClick: PropTypes.func.isRequired,
  horizontalScroll: PropTypes.number,
  printDisabled: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    paper: PropTypes.string.isRequired,
    paperVariantSingleDate: PropTypes.string.isRequired,
    shadowContainer: PropTypes.string.isRequired,
    shadow: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
    formVariantSingleDate: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
    prevNextControls: PropTypes.string.isRequired,
    iconPrint: PropTypes.string.isRequired,
    dateRangeFields: PropTypes.string.isRequired,
    tableContainer: PropTypes.string.isRequired,
    rowHead: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
    cellCaption: PropTypes.string.isRequired,
    cellCaptionVariantSingleDate: PropTypes.string.isRequired,
  }).isRequired,
};

ScheduleCalendarFilter.defaultProps = {
  horizontalScroll: 0,
  printDisabled: false,
};

export default reduxForm({
  onSubmit: () => {},
  validate: validateForm,
})(
  withStyles(styles)(ScheduleCalendarFilter),
);



// WEBPACK FOOTER //
// ./src/components/schedule/ScheduleCalendarFilter/ScheduleCalendarFilter.js